import React, { useContext, useEffect, useState } from "react"
import { View, StyleSheet, Text, Pressable } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import { DimensionsContext } from "@common/utils/mobileUtils"
import FAQComponent from "@common/components/FAQ/FAQComponent"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout, {
    funnelContentWidth,
} from "@common/components/funnel/FunnelStepLayout"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import FunnelEmbeddedCheckout from "../brevet/components/FunnelEmbeddedCheckout"
import { NewSubscriptionGuaranteesCard } from "../new-subscription/v2/components/NewSubscriptionGuaranteesCard"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import { formatEuros } from "../brevet/utils/formatEuros"
import { elide } from "@common/utils/activationSuiteDisplay"
import { useStageFunnel } from "./context/StageFunnelContext"
import { useStageTrack } from "./hooks/useStageTrack"
import { useStageFunnelGuard } from "./hooks/useStageFunnelGuard"
import { useStageFunnelConfig } from "./hooks/useStageFunnelConfig"
import { stageFunnelService } from "./services/stageFunnelService"
import {
    getTemplateName,
    getSlotsFromConfig,
    getSessionsFromConfig,
    groupSessionsByStart,
    sellsBySeance,
    discountPercent,
    getPricingByCount,
    pricingForCount,
    getFaqsFromConfig,
    getPreinscriptionCents,
    sessionFallbackLabel,
} from "./constants/stageContent"

// FAQ repliée par défaut — même mécanique que le checkout été.
const CheckoutFaqSection = ({ faqs }) => {
    const [open, setOpen] = useState(false)
    if (!faqs?.length) return null
    return (
        <View style={styles.faqSection}>
            <Pressable
                style={styles.faqToggle}
                onPress={() => setOpen((o) => !o)}
                accessibilityRole="button"
                accessibilityState={{ expanded: open }}
            >
                <FontAwesome5
                    name="question-circle"
                    solid
                    size={15}
                    color={theme.colors.primary}
                />
                <Text style={styles.faqToggleText}>Questions fréquentes</Text>
                <FontAwesome5
                    name={open ? "chevron-up" : "chevron-down"}
                    size={12}
                    color={theme.colors.grey}
                />
            </Pressable>
            {open ? <FAQComponent faqs={faqs} /> : null}
        </View>
    )
}

/**
 * Étape paiement 1 € du funnel Stage — RÉCAP LEAN, clone du checkout été adapté
 * au one-shot : le programme a été révélé juste avant, la composition (sessions
 * cochées × matières par session × horaire) est réglée. Ici, une seule chose à
 * faire : payer 1 € pour bloquer la place. Le solde du stage est prélevé au
 * démarrage (soir du 1ᵉʳ cours, présence-gaté — mécanisme brevet inchangé côté
 * backend).
 *
 * Tout le contenu chiffré (prix du count coché via pricing_by_count, dépôt,
 * échéance) vient du catalogue API — zéro constante ici. La FAQ est servie par
 * la config.
 */
const StageCheckoutScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useStageFunnel()
    const track = useStageTrack()
    // Refresh-safe / URL-partageable (voir useStageFunnelGuard).
    const { nextParams } = useStageFunnelGuard()

    const [clientSecret, setClientSecret] = useState(null)
    const [creatingSession, setCreatingSession] = useState(false)
    const [sessionError, setSessionError] = useState(null)
    // 422 catalogue au checkout ≠ erreur transitoire : la sélection n'est plus
    // vendable (stage clos, session/horaire plus offerts, cap matières).
    // Réessayer ne changerait rien — on bloque avec l'explication et le bon CTA
    // (retour composition), au lieu d'un « Réessayez » stérile.
    // { kind: "unavailable" | "invalid_selection", message }
    const [blockingError, setBlockingError] = useState(null)

    // Config catalogue : sessions / prix par count / horaires (libellés du
    // récap), FAQ, nom du stage.
    const { config } = useStageFunnelConfig(state.funnelStateId)
    const templateName = getTemplateName(config) || state.templateName
    const offeredSessions = getSessionsFromConfig(config)
    const isSeanceUnit = sellsBySeance(config)
    const pricingByCount = getPricingByCount(config)
    const slots = getSlotsFromConfig(config)
    const checkoutFaqs = getFaqsFromConfig(config)

    // Sélection effective : sessions cochées (triées) + matières par session.
    const selectedSessions = state.sessions || []
    const sessionSubjects = state.sessionSubjects || {}

    // Noms des matières du récap : d'abord ceux STAMPÉS en contexte à la
    // sélection (configurateur) → affichage INSTANTANÉ dans le flux avant, sans
    // attendre un fetch (sinon la matière « pop » sous la session). Le fetch de
    // secours ne part QUE si un nom manque encore — cas d'une reprise à froid
    // (le backend ne renvoie que les ids), où l'écran charge déjà de toute façon.
    const contextSubjectNames = state.subjectNames || {}
    const selectedSubjectIds = [
        ...new Set(selectedSessions.flatMap((d) => sessionSubjects[d] || [])),
    ].map(String)
    const missingSubjectName = selectedSubjectIds.some(
        (id) => !contextSubjectNames[id]
    )
    const [catalogSubjects, setCatalogSubjects] = useState([])
    useEffect(() => {
        if (!state.studentClass || !missingSubjectName) return
        let cancelled = false
        stageFunnelService
            .getStageSubjects({
                studentClass: state.studentClass,
                templateSlug: state.templateSlug,
            })
            .then((res) => {
                if (cancelled) return
                const list = res?.data?.data ?? res?.data ?? []
                setCatalogSubjects(Array.isArray(list) ? list : [])
            })
            .catch(() => {
                /* la ligne matières du récap est simplement omise */
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.studentClass, state.templateSlug, missingSubjectName])
    // Contexte d'abord (instantané), fetch de secours par-dessus (reprise froide).
    const subjectNameById = new Map(
        Object.entries(contextSubjectNames).map(([id, name]) => [
            String(id),
            name,
        ])
    )
    catalogSubjects.forEach((s) => subjectNameById.set(String(s.id), s.name))
    const subjectsLabelFor = (sessionDate) =>
        (sessionSubjects[sessionDate] || [])
            .map((id) => subjectNameById.get(String(id)))
            .filter(Boolean)
            .join(" + ")

    // Lignes du récap : une par SESSION cochée — une semaine en unité semaine,
    // un week-end en unité créneau, avec le détail de ses créneaux. Les
    // matières se lisent sur la session, pas sur l'item : en unité créneau
    // elles sont posées par week-end.
    //
    // Repli quand le catalogue n'est pas encore chargé : libellé construit
    // depuis la clé, une ligne par clé — jamais un récap vide devant un
    // paiement.
    const selectedOffered = offeredSessions.filter((s) =>
        selectedSessions.includes(s.key)
    )
    const recapSessions = selectedOffered.length
        ? groupSessionsByStart(selectedOffered).map((group) => ({
              date: group.start,
              label: isSeanceUnit
                  ? (group.label ?? group.items[0]?.label)
                  : group.items[0]?.label,
              // En unité créneau, dire QUELS créneaux — « Samedi 10h-12h ·
              // Dimanche 10h-12h » — sinon le parent paie sans les relire.
              detail: isSeanceUnit
                  ? group.items.map((i) => i.label).join(" · ")
                  : null,
              subjectsLabel: subjectsLabelFor(group.start),
          }))
        : selectedSessions.map((key, index) => ({
              date: key,
              label: sessionFallbackLabel(
                  { startDate: key.slice(0, 10), endDate: null },
                  index
              ),
              detail: null,
              subjectsLabel: subjectsLabelFor(key),
          }))

    const selectedSlot = slots.find((s) => s.key === state.slotKey) || null

    // Montants : total one-shot = prix du count coché (pricing_by_count) →
    // acompte = pré-inscription FIXE (1 €, ce qui est réellement encaissé
    // aujourd'hui) → solde au démarrage = remainder servi par le backend
    // (repli : total − 1 €). JAMAIS le deposit_cents catalogue à l'affichage.
    const pricing = pricingForCount(pricingByCount, selectedSessions.length)
    const priceCents = pricing?.priceCents ?? null
    // Remise calculée des deux montants — jamais servie par l'API.
    const discount = pricing
        ? discountPercent(pricing.priceCents, pricing.compareAtCents)
        : null
    const depositCents = getPreinscriptionCents(config)
    const remainderCents =
        pricing?.remainderAfterPreinscriptionCents ??
        (priceCents != null ? Math.max(0, priceCents - depositCents) : null)

    useEffect(() => {
        track("stage_funnel_checkout_viewed")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Création de la checkout session embarquée (1 €, carte sauvegardée pour le
    // solde). (Re)créée à chaque changement de la sélection effective — la
    // session DOIT suivre ce que le récap affiche.
    const selectionSignature = JSON.stringify({
        sessions: selectedSessions,
        sessionSubjects,
        slotKey: state.slotKey,
    })
    useEffect(() => {
        setClientSecret(null)
        setBlockingError(null)
        if (!state.funnelStateId) {
            setSessionError("Session expirée — reprenez depuis le début.")
            return
        }
        if (!selectedSessions.length) return

        let cancelled = false
        setCreatingSession(true)
        setSessionError(null)
        stageFunnelService
            .createStageCheckoutSession(
                { stateId: state.funnelStateId },
                {
                    sessions: selectedSessions,
                    sessionSubjects,
                    slotKey: state.slotKey,
                }
            )
            .then((res) => {
                if (cancelled) return
                const secret =
                    res?.data?.data?.client_secret ?? res?.data?.client_secret
                if (secret) {
                    setClientSecret(secret)
                } else {
                    setSessionError(
                        "Impossible d'initialiser le paiement. Réessayez."
                    )
                }
            })
            .catch((err) => {
                if (cancelled) return
                // Inscription déjà payée (retour arrière depuis la
                // confirmation, lien rouvert) : pas d'erreur, le parent
                // retrouve sa confirmation — il n'a rien à refaire.
                if (err?.response?.data?.error === "already_paid") {
                    update({ paid: true })
                    navigation.reset({
                        index: 0,
                        routes: [
                            { name: "stage/confirmation", params: nextParams },
                        ],
                    })
                    return
                }
                // 422 = refus catalogue (pas une erreur réseau) : stage clos ou
                // sélection plus offerte → état bloquant explicite, pas de retry.
                if (err?.response?.status === 422) {
                    const apiError = err?.response?.data?.error
                    setBlockingError(
                        apiError === "stage_unavailable"
                            ? { kind: "unavailable", message: null }
                            : {
                                  kind: "invalid_selection",
                                  message:
                                      typeof apiError === "string"
                                          ? apiError
                                          : null,
                              }
                    )
                    return
                }
                setSessionError(
                    "Impossible d'initialiser le paiement. Réessayez."
                )
            })
            .finally(() => {
                if (!cancelled) setCreatingSession(false)
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.funnelStateId, selectionSignature])

    const onPaymentSuccess = (paymentIntentId) => {
        track("stage_funnel_payment_succeeded", {
            nb_sessions: selectedSessions.length,
        })
        update({ paid: true, paymentIntentId })
        navigation.navigate("stage/confirmation", nextParams)
    }

    const onPaymentError = (errorCode) => {
        track("stage_funnel_payment_failed", {
            error_code: errorCode || "unknown",
        })
    }

    // Unique affordance d'édition : renvoie au configurateur.
    const onEditChoices = () => {
        track("stage_funnel_edit_choices_clicked")
        navigation.navigate("stage/configurator", nextParams)
    }

    // Deux enfants du checkout Stripe sont dimensionnés en dur : ils doivent
    // tenir dans la colonne du gabarit, pas dans la fenêtre.
    const containerWidth = funnelContentWidth(dimensions?.width || 400)
    const firstname = state.studentFirstname || "votre enfant"

    // Bloc commercial « Réservez en confiance » — pourquoi 1 € + garanties.
    const commercialReassuranceItems = [
        {
            icon: "lock",
            label: "Pourquoi 1 € aujourd'hui ?",
            subtitle: `Pour bloquer la place ${elide(firstname)} dans son groupe (places limitées). Le solde du stage n'est prélevé qu'au démarrage.`,
        },
        {
            icon: "calendar-check",
            label: "Annulable sans condition avant le début du stage",
            subtitle: "Vous changez d'avis ? Aucun frais, aucune question.",
        },
        {
            icon: "user-friends",
            label: "Un tuteur attribué avant le 1ᵉʳ cours",
            subtitle: "Choisi pour sa maîtrise des matières du programme.",
        },
    ]

    const summary = (
        <>
            <Text style={styles.title}>
                Réservez la place {elide(firstname)}
            </Text>
            <Text style={styles.subtitle}>
                Dernière étape — on bloque la place pour 1 €.
            </Text>

            {/* Récap : le stage, puis UNE ligne par session cochée (libellé +
                ses matières), puis l'horaire. Plus de « formule » ni de « date
                de départ » — la sélection EST la liste des sessions. */}
            <View style={styles.recapCard}>
                <View style={styles.recapHeader}>
                    <Text style={styles.recapTitle}>Votre sélection</Text>
                    <Text
                        style={styles.recapEdit}
                        onPress={onEditChoices}
                        accessibilityRole="button"
                    >
                        Modifier
                    </Text>
                </View>
                {templateName ? (
                    <View style={styles.recapLine}>
                        <FontAwesome5
                            name="flag"
                            solid
                            size={11}
                            color={theme.colors.grey}
                            style={styles.recapIcon}
                        />
                        <Text style={styles.recapLineLabel}>Stage</Text>
                        <Text style={styles.recapLineValue} numberOfLines={2}>
                            {templateName}
                        </Text>
                    </View>
                ) : null}
                {recapSessions.map((s) => (
                    <View key={s.date} style={styles.recapLine}>
                        <FontAwesome5
                            name="calendar-week"
                            solid
                            size={11}
                            color={theme.colors.grey}
                            style={styles.recapIcon}
                        />
                        <View style={styles.recapSessionTexts}>
                            <Text
                                style={styles.recapLineValue}
                                numberOfLines={2}
                            >
                                {s.label}
                            </Text>
                            {/* Unité créneau : dire QUELS créneaux, sinon le
                                parent paie sans pouvoir les relire. */}
                            {s.detail ? (
                                <Text
                                    style={styles.recapSessionSubjects}
                                    numberOfLines={2}
                                >
                                    {s.detail}
                                </Text>
                            ) : null}
                            {s.subjectsLabel ? (
                                <Text
                                    style={styles.recapSessionSubjects}
                                    numberOfLines={2}
                                >
                                    {s.subjectsLabel}
                                </Text>
                            ) : null}
                        </View>
                    </View>
                ))}
                {/* En unité créneau, l'horaire est la grille de ce qui est
                    achetable, pas un choix du parent — et les créneaux payés
                    sont déjà détaillés ligne par ligne au-dessus. L'afficher
                    ici annoncerait des séances non achetées. */}
                {selectedSlot && !isSeanceUnit ? (
                    <View style={styles.recapLine}>
                        <FontAwesome5
                            name="clock"
                            solid
                            size={11}
                            color={theme.colors.grey}
                            style={styles.recapIcon}
                        />
                        <Text style={styles.recapLineLabel}>Horaire</Text>
                        <Text style={styles.recapLineValue} numberOfLines={2}>
                            {selectedSlot.label ||
                                selectedSlot.sessionLabels.join(" · ")}
                        </Text>
                    </View>
                ) : null}
            </View>

            {/* Prix en un bloc clair : 1 € aujourd'hui → solde au démarrage. */}
            <View style={styles.priceCard}>
                <Text style={styles.heroTodayLabel}>Aujourd'hui</Text>
                <Text style={styles.heroTodayValue}>
                    {formatEuros(depositCents / 100)}
                </Text>
                {remainderCents != null ? (
                    <Text style={styles.heroThen}>
                        puis {formatEuros(remainderCents / 100)} — solde prélevé
                        le lendemain du 1ᵉʳ cours, une fois la séance tenue
                    </Text>
                ) : (
                    <Text style={styles.heroThen}>
                        Le solde du stage est prélevé le lendemain du 1ᵉʳ cours,
                        une fois la séance tenue — montant détaillé dans l'email
                        de confirmation.
                    </Text>
                )}
                {priceCents != null ? (
                    <Text style={styles.heroTotal}>
                        Total du stage :{" "}
                        {/* La promo se lit AUSSI ici : c'est le dernier écran
                            avant de payer, et celui qui doit justifier le
                            tarif régulier au moment de la conversion. */}
                        {discount != null ? (
                            <Text style={styles.heroTotalCompare}>
                                {formatEuros(pricing.compareAtCents / 100)}{" "}
                            </Text>
                        ) : null}
                        {formatEuros(priceCents / 100)}
                        {discount != null ? ` (−${discount} %)` : ""}
                    </Text>
                ) : null}
                <View style={styles.priceDivider} />
                <View style={styles.priceDetailRow}>
                    <FontAwesome5
                        name="video"
                        size={12}
                        color={theme.colors.grey}
                        style={styles.priceDetailIcon}
                    />
                    <Text style={styles.priceDetailText}>
                        Cours en visio sur notre plateforme
                    </Text>
                </View>
            </View>

            {/* Un seul bloc commercial scannable : pourquoi 1 € + garanties. */}
            <View style={styles.guaranteesWrap}>
                <NewSubscriptionGuaranteesCard
                    title="Réservez en confiance"
                    items={commercialReassuranceItems}
                />
            </View>
        </>
    )

    // ===== État bloquant (422 catalogue) : pas de paiement possible =====
    // Même pattern que l'écran « stage indisponible » de StageClassScreen :
    // l'explication + le bon CTA, jamais un « Réessayez » stérile.
    if (blockingError) {
        const isUnavailable = blockingError.kind === "unavailable"
        return (
            <FunnelStepLayout
                scene={FUNNEL_SCENES.checkout}
                scroll={false}
                header={
                    <FunnelStepHeader
                        step={6}
                        total={6}
                        onBack={() => navigation.goBack()}
                    />
                }
            >
                <View style={styles.blockingFrame}>
                    <View style={styles.blockingIcon}>
                        <FontAwesome5
                            name={isUnavailable ? "calendar-times" : "sync-alt"}
                            size={28}
                            color={theme.colors.primary}
                        />
                    </View>
                    <Text style={styles.blockingTitle}>
                        {isUnavailable
                            ? "Ce stage n'est plus disponible"
                            : "Votre sélection n'est plus disponible"}
                    </Text>
                    <Text style={styles.blockingText}>
                        {isUnavailable
                            ? `Les inscriptions pour ${templateName || "ce stage"} sont fermées pour le moment. Contactez-nous pour connaître les prochaines dates.`
                            : blockingError.message ||
                              "Un élément de votre sélection (semaine, matières ou horaire) n'est plus proposé. Modifiez votre sélection pour continuer."}
                    </Text>
                    {!isUnavailable && (
                        <FilledButtonM
                            label="Modifier ma sélection"
                            onPress={onEditChoices}
                            color={theme.colors.primary}
                            style={styles.blockingCta}
                        />
                    )}
                    <SummerHelpBanner
                        track={track}
                        step={6}
                        trackingEvent="stage_funnel_help_clicked"
                        style={styles.blockingHelp}
                    />
                </View>
            </FunnelStepLayout>
        )
    }

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.checkout}
            scroll={false}
            header={
                <FunnelStepHeader
                    step={6}
                    total={6}
                    onBack={() => navigation.goBack()}
                />
            }
        >
            {({ scrollProps, spacer }) => (
                <FunnelEmbeddedCheckout
                    clientSecret={clientSecret}
                    scrollProps={scrollProps}
                    summary={
                        <>
                            {spacer}
                            {summary}
                        </>
                    }
                    buttonLabel="Valider l'inscription · 1 €"
                    guarantees={[]}
                    secureBadge
                    belowContent={
                        <>
                            <CheckoutFaqSection faqs={checkoutFaqs} />
                            <SummerHelpBanner
                                track={track}
                                step={6}
                                trackingEvent="stage_funnel_help_clicked"
                            />
                        </>
                    }
                    // FAQ + aide AUSSI quand le paiement ne s'initialise pas
                    // (préparation / erreur session) : c'est précisément là que
                    // le parent a besoin du numéro de téléphone.
                    fallbackBelowContent={
                        <>
                            <CheckoutFaqSection faqs={checkoutFaqs} />
                            <SummerHelpBanner
                                track={track}
                                step={6}
                                trackingEvent="stage_funnel_help_clicked"
                            />
                        </>
                    }
                    onSuccess={onPaymentSuccess}
                    onError={onPaymentError}
                    creatingSession={creatingSession}
                    sessionError={sessionError}
                    containerWidth={containerWidth}
                    nativeNotice="Le paiement se finalise depuis un navigateur. Rouvrez le lien sur un ordinateur pour terminer votre réservation."
                />
            )}
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 6,
    },
    subtitle: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        marginBottom: 16,
    },

    // Récap : carte avec une ligne par dimension.
    recapCard: {
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.whiteAlmost,
        paddingVertical: 12,
        paddingHorizontal: 14,
        marginBottom: 16,
        gap: 8,
    },
    recapHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 2,
    },
    recapTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        color: theme.colors.primary,
    },
    recapLine: {
        flexDirection: "row",
        alignItems: "flex-start",
    },
    recapIcon: {
        width: 16,
        marginTop: 2,
        marginRight: 6,
    },
    recapLineLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.grey,
        width: 72,
    },
    recapLineValue: {
        flex: 1,
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
        lineHeight: 17,
    },
    // Ligne session : libellé catalogue + matières de LA session en dessous.
    recapSessionTexts: { flex: 1 },
    recapSessionSubjects: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        lineHeight: 15,
        marginTop: 1,
    },
    recapEdit: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
        textDecorationLine: "underline",
    },

    // FAQ repliable « Questions fréquentes ».
    faqSection: {
        marginTop: 8,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
    },
    faqToggle: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 16,
        paddingHorizontal: 4,
    },
    faqToggleText: {
        flex: 1,
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 15,
        color: theme.colors.primary,
    },

    // Bloc prix.
    priceCard: {
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        padding: 16,
        backgroundColor: theme.colors.whiteAlmost,
    },
    heroTodayLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.grey,
        textAlign: "center",
    },
    heroTodayValue: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 44,
        lineHeight: 50,
        color: theme.colors.success || "#00BE89",
        textAlign: "center",
    },
    heroThen: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        textAlign: "center",
        marginTop: 2,
    },
    heroTotal: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.grey,
        textAlign: "center",
        marginTop: 6,
    },
    // Aligné sur le barré du funnel brevet (`summaryStrike`) : discret, il met
    // en valeur le prix de lancement au lieu de lui faire concurrence.
    heroTotalCompare: {
        fontFamily: "Montserrat-Regular",
        color: theme.colors.light,
        textDecorationLine: "line-through",
    },
    priceDivider: {
        height: 1,
        backgroundColor: theme.colors.lightMore,
        marginVertical: 12,
    },
    priceDetailRow: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 3,
    },
    priceDetailIcon: { width: 20 },
    priceDetailText: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 16,
    },

    guaranteesWrap: { marginTop: 20 },

    // --- État bloquant (422 catalogue au checkout) ---
    blockingFrame: {
        flex: 1,
        alignItems: "center",
        paddingHorizontal: 4,
        paddingTop: 40,
    },
    blockingIcon: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: theme.colors.primaryLighter,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
    },
    blockingTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 20,
        color: theme.colors.primary,
        textAlign: "center",
        marginBottom: 8,
    },
    blockingText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 14,
        lineHeight: 20,
        color: theme.colors.grey,
        textAlign: "center",
        marginBottom: 16,
    },
    blockingCta: { width: "100%", marginBottom: 8 },
    blockingHelp: { alignSelf: "stretch" },
})

export default StageCheckoutScreen
